import { useState, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client/react';
import { gql } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_MY_TASKS, GET_MY_DOCUMENTS } from '../graphql/clientQueries';
import { COMPLETE_MY_TASK, GET_DOCUMENT_UPLOAD_URL, CREATE_DOCUMENT } from '../graphql/clientMutations';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileViewerModal, FileItem } from '../../components/FileViewerModal';
import {
  validateFileForUpload,
  getFileValidationErrorMessage,
  getFileInputAcceptAttribute,
} from '../../utils/file-validation';
import type { Task, TaskStatus, TaskPriority, TaskType, Document } from '../types';

const GET_DOCUMENT_DOWNLOAD_URL = gql`
  query GetDocumentDownloadUrl($id: ID!) {
    getDocumentDownloadUrl(id: $id)
  }
`;

interface GetMyTasksResponse {
  listMyTasks: {
    items: Task[];
  };
}

interface GetMyDocumentsResponse {
  listDocumentsByCase: Document[];
}

interface GetDocumentUploadUrlResponse {
  getDocumentUploadUrl: { uploadUrl: string; s3Key: string };
}

interface CreateDocumentResponse {
  createDocument: Document;
}

interface CompleteTaskResponse {
  completeTask: {
    id: string;
    status: TaskStatus;
    completedAt: string;
    clientNotes?: string;
  };
}

type FilterStatus = 'all' | 'pending' | 'completed' | 'overdue';

const STATUS_CONFIG: Record<TaskStatus | 'OVERDUE', { label: string; color: string; bgColor: string }> = {
  PENDING: { label: 'Pending', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  IN_PROGRESS: { label: 'In Progress', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  COMPLETED: { label: 'Awaiting Review', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  VERIFIED: { label: 'Verified', color: 'text-green-700', bgColor: 'bg-green-100' },
  REJECTED: { label: 'Needs Revision', color: 'text-red-700', bgColor: 'bg-red-100' },
  OVERDUE: { label: 'Overdue', color: 'text-white', bgColor: 'bg-red-500' },
};

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string; bgColor: string }> = {
  LOW: { label: 'Low', color: 'text-gray-600', bgColor: 'bg-gray-100' },
  MEDIUM: { label: 'Medium', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  HIGH: { label: 'High', color: 'text-amber-600', bgColor: 'bg-amber-100' },
  URGENT: { label: 'Urgent', color: 'text-red-600', bgColor: 'bg-red-100' },
};

export function TasksScreen() {
  const apolloClient = useApolloClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [clientNotes, setClientNotes] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetMyTasksResponse>(GET_MY_TASKS, {
    fetchPolicy: 'cache-and-network',
  });

  const [completeTask] = useMutation<CompleteTaskResponse>(COMPLETE_MY_TASK);

  const tasks = data?.listMyTasks?.items ?? [];

  const isTaskOverdue = useCallback((task: Task): boolean => {
    if (task.status === 'COMPLETED' || task.status === 'VERIFIED') return false;
    const dueDate = new Date(task.dueDate);
    const now = new Date();
    return dueDate < now;
  }, []);

  const { pendingTasks, completedTasks, overdueTasks, rejectedTasks, filteredTasks } = useMemo(() => {
    const pending = tasks.filter(
      (t) => (t.status === 'PENDING' || t.status === 'IN_PROGRESS') && !isTaskOverdue(t)
    );
    const completed = tasks.filter((t) => t.status === 'COMPLETED' || t.status === 'VERIFIED');
    const overdue = tasks.filter(
      (t) => (t.status === 'PENDING' || t.status === 'IN_PROGRESS') && isTaskOverdue(t)
    );
    const rejected = tasks.filter((t) => t.status === 'REJECTED');

    let filtered: Task[];
    switch (filterStatus) {
      case 'pending':
        filtered = [...overdue, ...pending, ...rejected];
        break;
      case 'completed':
        filtered = completed;
        break;
      case 'overdue':
        filtered = overdue;
        break;
      default:
        filtered = tasks;
    }

    return { pendingTasks: pending, completedTasks: completed, overdueTasks: overdue, rejectedTasks: rejected, filteredTasks: filtered };
  }, [tasks, filterStatus, isTaskOverdue]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);

  const openTaskDetail = (task: Task) => {
    setSelectedTask(task);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedTask(null);
  };

  const openCompleteModal = () => {
    setShowDetailModal(false);
    setShowCompleteModal(true);
    setClientNotes('');
  };

  const closeCompleteModal = () => {
    setShowCompleteModal(false);
    setClientNotes('');
  };

  const handleDocumentsUploaded = async () => {
    await refetch();
  };

  const handleCompleteTask = async () => {
    if (!selectedTask) return;

    setIsCompleting(true);
    try {
      await completeTask({
        variables: {
          id: selectedTask.id,
          clientNotes: clientNotes.trim() || undefined,
        },
      });

      toast.success('Task submitted for review');
      closeCompleteModal();
      setSelectedTask(null);
      await refetch();
    } catch (err) {
      console.error('Error completing task:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to complete task');
    } finally {
      setIsCompleting(false);
    }
  };

  const getStatusConfig = (task: Task) => {
    if (isTaskOverdue(task)) return STATUS_CONFIG.OVERDUE;
    return STATUS_CONFIG[task.status] || STATUS_CONFIG.PENDING;
  };

  const formatTaskType = (taskType: TaskType): string => {
    return taskType.replace(/_/g, ' ');
  };

  const getDaysUntilDue = (dueDate: string): string => {
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Loading state
  if (loading && !data) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col items-center justify-center p-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-aalaw-red mb-4"></div>
        <p className="text-gray-500">Loading tasks...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col items-center justify-center p-6">
        <div className="text-6xl mb-4">😕</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load tasks</h2>
        <p className="text-gray-600 mb-4">{error.message}</p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">My Tasks</h1>
            <p className="text-sm text-gray-500">
              {pendingTasks.length + overdueTasks.length + rejectedTasks.length} pending • {completedTasks.length} completed
            </p>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-aalaw-red focus:border-transparent bg-white"
            >
              <option value="all">All Tasks</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="overdue">Overdue</option>
            </select>

            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
              {isRefreshing ? (
                <span className="animate-spin mr-2">↻</span>
              ) : (
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 overscroll-contain scrollbar-visible" style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* Overdue Warning Banner */}
        {overdueTasks.length > 0 && filterStatus !== 'completed' && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-red-800 text-sm">
                {overdueTasks.length} overdue task{overdueTasks.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-red-600">Please complete as soon as possible</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">📝</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Tasks Yet</h2>
            <p className="text-gray-500 text-center max-w-md">
              Your attorney will assign tasks as your case progresses.
            </p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No {filterStatus} tasks</h2>
            <p className="text-gray-500 text-center">
              {filterStatus === 'overdue'
                ? "Great job! You don't have any overdue tasks."
                : filterStatus === 'pending'
                ? 'All tasks are completed.'
                : 'No completed tasks yet.'}
            </p>
          </div>
        ) : (
          /* Task Grid - 2 columns on desktop, 1 on mobile */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredTasks.map((task) => {
              const statusConfig = getStatusConfig(task);
              const priorityConfig = PRIORITY_CONFIG[task.priority];
              const taskIsOverdue = isTaskOverdue(task);

              return (
                <button
                  key={task.id}
                  onClick={() => openTaskDetail(task)}
                  className={`w-full text-left bg-white border rounded-xl p-4 transition-all hover:shadow-md hover:border-gray-300 ${
                    taskIsOverdue ? 'border-red-300 bg-red-50/30' : 'border-gray-200'
                  }`}
                >
                  {/* Header: Title + Status */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="font-semibold text-gray-900 line-clamp-2 flex-1">{task.title}</h3>
                    <Badge className={`${statusConfig.bgColor} ${statusConfig.color} border-0 text-xs flex-shrink-0`}>
                      {statusConfig.label}
                    </Badge>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{task.description}</p>

                  {/* Case/Inquiry Info */}
                  {task.case && (
                    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs mb-3 ${
                      task.case.status === 'INQUIRY' 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {task.case.status === 'INQUIRY' ? (
                        <>
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Inquiry: {task.case.caseType}</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                          <span>Case: {task.case.caseNumber}</span>
                        </>
                      )}
                    </div>
                  )}

                  {/* Footer: Priority, Type, Due Date */}
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
                    <Badge className={`${priorityConfig.bgColor} ${priorityConfig.color} border-0 text-xs`}>
                      {priorityConfig.label}
                    </Badge>
                    <span className="text-xs text-gray-500">{formatTaskType(task.taskType)}</span>
                    <span className="flex-1" />
                    <span className={`text-xs font-medium ${taskIsOverdue ? 'text-red-600' : 'text-gray-500'}`}>
                      {getDaysUntilDue(task.dueDate)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Task Detail Modal */}
      {showDetailModal && selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          isOverdue={isTaskOverdue(selectedTask)}
          onClose={closeDetailModal}
          onComplete={openCompleteModal}
          onDocumentsUploaded={handleDocumentsUploaded}
          apolloClient={apolloClient}
          getStatusConfig={getStatusConfig}
          formatTaskType={formatTaskType}
          formatDate={formatDate}
          getDaysUntilDue={getDaysUntilDue}
        />
      )}

      {/* Complete Task Modal */}
      {showCompleteModal && selectedTask && (
        <CompleteTaskModal
          task={selectedTask}
          clientNotes={clientNotes}
          setClientNotes={setClientNotes}
          isCompleting={isCompleting}
          onClose={closeCompleteModal}
          onComplete={handleCompleteTask}
        />
      )}
    </div>
  );
}


// Task Detail Modal Component
interface TaskDetailModalProps {
  task: Task;
  isOverdue: boolean;
  onClose: () => void;
  onComplete: () => void;
  onDocumentsUploaded: () => void;
  apolloClient: ReturnType<typeof useApolloClient>;
  getStatusConfig: (task: Task) => { label: string; color: string; bgColor: string };
  formatTaskType: (type: TaskType) => string;
  formatDate: (date: string) => string;
  getDaysUntilDue: (date: string) => string;
}

function TaskDetailModal({
  task,
  isOverdue,
  onClose,
  onComplete,
  onDocumentsUploaded,
  apolloClient,
  getStatusConfig,
  formatTaskType,
  formatDate,
  getDaysUntilDue,
}: TaskDetailModalProps) {
  const statusConfig = getStatusConfig(task);
  const priorityConfig = PRIORITY_CONFIG[task.priority];
  const canComplete = task.status === 'PENDING' || task.status === 'IN_PROGRESS' || task.status === 'REJECTED';
  const isDocumentUploadTask = task.taskType === 'DOCUMENT_UPLOAD';

  // Document upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerFiles, setViewerFiles] = useState<FileItem[]>([]);

  const { data: documentsData, loading: documentsLoading, refetch: refetchDocuments } = useQuery<GetMyDocumentsResponse>(
    GET_MY_DOCUMENTS,
    {
      variables: { caseId: task.caseId },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [getUploadUrl] = useMutation<GetDocumentUploadUrlResponse>(GET_DOCUMENT_UPLOAD_URL);
  const [createDocument] = useMutation<CreateDocumentResponse>(CREATE_DOCUMENT);

  const recentDocuments = useMemo(() => {
    const docs = documentsData?.listDocumentsByCase ?? [];
    return [...docs]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);
  }, [documentsData]);

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    const validationResult = validateFileForUpload(file.name, file.type, file.size);
    if (!validationResult.isValid) {
      toast.error(getFileValidationErrorMessage(validationResult) || 'Invalid file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    try {
      const { data: uploadUrlData } = await getUploadUrl({
        variables: {
          input: { caseId: task.caseId, fileName: file.name, fileType: file.type, fileSize: file.size },
        },
      });

      if (!uploadUrlData?.getDocumentUploadUrl?.uploadUrl) throw new Error('Failed to get upload URL');
      setUploadProgress(30);

      const { uploadUrl, s3Key } = uploadUrlData.getDocumentUploadUrl;
      const uploadResponse = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!uploadResponse.ok) throw new Error('Failed to upload file');
      setUploadProgress(70);

      await createDocument({
        variables: {
          input: { caseId: task.caseId, fileName: file.name, fileType: file.type, fileSize: file.size, s3Key, description: `Uploaded for task: ${task.title}` },
        },
      });

      setUploadProgress(100);
      toast.success('Document uploaded');
      await refetchDocuments();
      onDocumentsUploaded();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handlePreviewDocument = async (doc: Document) => {
    try {
      const result = await apolloClient.query<{ getDocumentDownloadUrl: string }>({
        query: GET_DOCUMENT_DOWNLOAD_URL,
        variables: { id: doc.id },
        fetchPolicy: 'network-only',
      });
      const url = result?.data?.getDocumentDownloadUrl;
      if (url) {
        setViewerFiles([{ url, fileName: doc.fileName, fileType: doc.fileType }]);
        setViewerVisible(true);
      }
    } catch {
      toast.error('Failed to load document');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileIcon = (fileType: string): string => {
    if (!fileType) return '📎';
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word')) return '📝';
    return '📎';
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
          {/* Header */}
          <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-gray-900 mb-2">{task.title}</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={`${statusConfig.bgColor} ${statusConfig.color} border-0 text-xs`}>
                    {statusConfig.label}
                  </Badge>
                  <Badge className={`${priorityConfig.bgColor} ${priorityConfig.color} border-0 text-xs`}>
                    {priorityConfig.label} Priority
                  </Badge>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0">
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Case/Inquiry Info - PROMINENT */}
            {task.case && (
              <div className={`p-4 rounded-xl border ${
                task.case.status === 'INQUIRY' 
                  ? 'bg-amber-50 border-amber-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    task.case.status === 'INQUIRY' ? 'bg-amber-100' : 'bg-blue-100'
                  }`}>
                    {task.case.status === 'INQUIRY' ? (
                      <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className={`text-xs font-medium uppercase tracking-wide ${
                      task.case.status === 'INQUIRY' ? 'text-amber-600' : 'text-blue-600'
                    }`}>
                      {task.case.status === 'INQUIRY' ? 'Related to Your Inquiry' : 'Related Case'}
                    </p>
                    <p className={`text-base font-semibold ${
                      task.case.status === 'INQUIRY' ? 'text-amber-900' : 'text-blue-900'
                    }`}>
                      {task.case.status === 'INQUIRY' ? task.case.caseType : task.case.caseNumber}
                    </p>
                    {task.case.status !== 'INQUIRY' && task.case.caseType && (
                      <p className="text-sm text-blue-700">{task.case.caseType}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{task.description}</p>
            </div>

            {/* Task Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Due Date
                </p>
                <p className="text-sm font-semibold text-gray-900">{formatDate(task.dueDate)}</p>
                <p className={`text-xs mt-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                  {getDaysUntilDue(task.dueDate)}
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Task Type
                </p>
                <p className="text-sm font-semibold text-gray-900">{formatTaskType(task.taskType)}</p>
                <p className="text-xs text-gray-500 mt-1">By {task.assignedByName || 'Your Attorney'}</p>
              </div>
            </div>

            {/* Status Messages */}
            {task.status === 'COMPLETED' && (
              <div className="flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-xl">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-purple-800">Awaiting Review</p>
                  <p className="text-sm text-purple-700">Your submission is being reviewed by staff</p>
                </div>
              </div>
            )}

            {task.status === 'VERIFIED' && (
              <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-green-800">Verified</p>
                  <p className="text-sm text-green-700">
                    Completed on {task.verifiedAt ? new Date(task.verifiedAt).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            )}

            {task.status === 'REJECTED' && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-red-800">Needs Revision</p>
                  <p className="text-sm text-red-700">{task.rejectionReason || 'Please review and resubmit'}</p>
                </div>
              </div>
            )}

            {/* Client Notes */}
            {task.clientNotes && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Your Notes</h3>
                <p className="text-sm text-gray-700">{task.clientNotes}</p>
              </div>
            )}

            {/* Document Upload Section */}
            {isDocumentUploadTask && canComplete && (
              <div className="border-t border-gray-200 pt-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">Upload Documents</h3>
                  <button
                    onClick={handleUploadClick}
                    disabled={isUploading}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {isUploading ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    )}
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </button>
                  <input ref={fileInputRef} type="file" accept={getFileInputAcceptAttribute()} onChange={handleFileSelect} className="hidden" />
                </div>

                {isUploading && (
                  <div className="mb-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-center">Uploading... {uploadProgress}%</p>
                  </div>
                )}

                <div className="space-y-2">
                  {documentsLoading ? (
                    <div className="flex justify-center py-4">
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                    </div>
                  ) : recentDocuments.length > 0 ? (
                    <>
                      <p className="text-xs text-gray-500 mb-2">Recent uploads for this case:</p>
                      {recentDocuments.map((doc) => (
                        <button
                          key={doc.id}
                          onClick={() => handlePreviewDocument(doc)}
                          className="w-full flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left"
                        >
                          <span className="text-lg">{getFileIcon(doc.fileType)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{doc.fileName}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(doc.fileSize)} • {new Date(doc.createdAt).toLocaleDateString()}</p>
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      ))}
                    </>
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded-lg">
                      <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm text-gray-500">No documents uploaded yet</p>
                      <p className="text-xs text-gray-400 mt-1">Click "Upload" to add documents</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-gray-50 space-y-2">
            {canComplete && (
              <Button onClick={onComplete} className="w-full bg-green-600 hover:bg-green-700 text-white h-11">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {task.status === 'REJECTED' ? 'Resubmit Task' : 'Mark as Complete'}
              </Button>
            )}
            <Button variant="outline" onClick={onClose} className="w-full h-11">
              Close
            </Button>
          </div>
        </div>
      </div>

      <FileViewerModal visible={viewerVisible} files={viewerFiles} initialIndex={0} onClose={() => setViewerVisible(false)} />
    </>
  );
}


// Complete Task Modal
interface CompleteTaskModalProps {
  task: Task;
  clientNotes: string;
  setClientNotes: (notes: string) => void;
  isCompleting: boolean;
  onClose: () => void;
  onComplete: () => void;
}

function CompleteTaskModal({ task, clientNotes, setClientNotes, isCompleting, onClose, onComplete }: CompleteTaskModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Complete Task</h2>
            <button onClick={onClose} disabled={isCompleting} className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50">
              <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Task Summary */}
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="font-medium text-gray-900 mb-1">{task.title}</p>
            {task.case && (
              <p className="text-sm text-gray-600">
                {task.case.status === 'INQUIRY' 
                  ? `Inquiry: ${task.case.caseType}` 
                  : `Case: ${task.case.caseNumber}`}
              </p>
            )}
          </div>

          {/* Notes Input */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Add Notes (Optional)</label>
            <p className="text-xs text-gray-500 mb-2">Share any details about how you completed this task</p>
            <textarea
              value={clientNotes}
              onChange={(e) => setClientNotes(e.target.value)}
              placeholder="e.g., Uploaded 3 medical bills from Dr. Smith..."
              rows={4}
              disabled={isCompleting}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none disabled:bg-gray-100"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex gap-3">
          <Button variant="outline" onClick={onClose} disabled={isCompleting} className="flex-1 h-11">
            Cancel
          </Button>
          <Button onClick={onComplete} disabled={isCompleting} className="flex-1 bg-green-600 hover:bg-green-700 text-white h-11">
            {isCompleting ? (
              <>
                <span className="animate-spin mr-2">↻</span>
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Submit for Review
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
